/**
 * Galeria de modelos prontos (issue #137) — referência visual: GERU.
 *
 * Cada card mostra o fluxo do modelo como uma sequência de passos, para o
 * usuário entender o que ele faz antes de usar. "Usar modelo" cria a automação
 * (inativa) e abre no editor para ajustar o que falta (etapa, texto).
 */

import { useMemo, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { metaFor } from './nodeMeta.js'

// Passos do modelo na ordem do fluxo (gatilho primeiro), para a faixa do card.
function passos(definition) {
  const nodes = definition?.nodes || []
  const edges = definition?.edges || []
  const incoming = new Set(edges.map((e) => e.target))
  const ordem = []
  const fila = nodes.filter((n) => !incoming.has(n.id)).map((n) => n.id)
  const vistos = new Set()
  while (fila.length) {
    const id = fila.shift()
    if (vistos.has(id)) continue
    vistos.add(id)
    ordem.push(nodes.find((n) => n.id === id))
    edges.filter((e) => e.source === id).forEach((e) => fila.push(e.target))
  }
  return ordem.filter(Boolean)
}

export default function AutomacaoTemplates({ templates, catalog, busyKey, onUse, onBlank }) {
  const categorias = useMemo(
    () => ['Todos', ...new Set(templates.map((t) => t.categoria))],
    [templates],
  )
  const [categoria, setCategoria] = useState('Todos')
  const visiveis = categoria === 'Todos' ? templates : templates.filter((t) => t.categoria === categoria)

  return (
    <div className="aut-gallery">
      <div className="aut-chips" role="tablist" aria-label="Categorias de modelos">
        {categorias.map((c) => (
          <button
            key={c} type="button" role="tab" aria-selected={c === categoria}
            className={`aut-chip${c === categoria ? ' is-active' : ''}`}
            onClick={() => setCategoria(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="aut-gallery-grid">
        <button type="button" className="aut-tpl aut-tpl-blank" onClick={onBlank} disabled={!!busyKey}>
          <span className="aut-tpl-blank-icon"><Icon n="plus" s={22} /></span>
          <strong>Começar do zero</strong>
          <span>Monte o fluxo passo a passo no editor.</span>
        </button>

        {visiveis.map((t) => (
          <article key={t.key} className="aut-tpl">
            <header className="aut-tpl-head">
              <span className="aut-tpl-icon"><Icon n={t.icon || 'zap'} s={18} /></span>
              <span className="badge badge-neutral">{t.categoria}</span>
            </header>
            <h3>{t.nome}</h3>
            <p>{t.descricao}</p>
            <ol className="aut-tpl-flow">
              {passos(t.definition).map((n, i) => {
                const type = catalog[n.data.nodeType]
                const meta = metaFor(type)
                return (
                  <li key={n.id} className={`tone-${meta.tone}`}>
                    {i > 0 && <Icon n="chevron_r" s={12} c="var(--ter)" />}
                    <span className="aut-tpl-step"><Icon n={meta.icon} s={12} /> {type?.label || n.data.nodeType}</span>
                  </li>
                )
              })}
            </ol>
            <button
              type="button" className="btn btn-black btn-sm aut-tpl-use"
              onClick={() => onUse(t)} disabled={!!busyKey}
            >
              {busyKey === t.key ? 'Criando…' : 'Usar modelo'}
            </button>
          </article>
        ))}
      </div>
    </div>
  )
}
